import * as S from './styles'
import { Outlet, useLocation } from 'react-router-dom'
import Menu from 'components/Container/Menu'
import TopBar from 'components/Container/TopBar'

import LogoutModal from 'components/Container/LogoutModal'
import CompanyBlockedOverlay from 'components/shared/CompanyBlockedOverlay'
import { checkIsLogged } from 'utils/UserAuthorization'
import { Suspense, useState, useEffect, useRef } from 'react'
import Loader from 'components/Loader'
import CarregamentoGlobal from 'components/Loader/CarregamentoGlobal'
import { preloadRoute } from 'components/Router/routeLoaders'
import Sofia from 'components/Container/Sofia'
import ConvitePush from 'components/shared/ConvitePush'

// Telas que ocupam a altura da janela e rolam só por dentro (lista de
// publicações, colunas do quadro): a página em si não rola, para não haver
// duas rolagens ao mesmo tempo
const TELAS_FIXAS = ['/publications', '/legal-kanban']

const TELAS_PRINCIPAIS = ['/dashboard', '/schedule', '/legal-kanban', '/publications', '/active-processes', '/clients', '/financeiro']

function Container() {
  const isLogged = checkIsLogged()
  const { pathname } = useLocation()
  // A cada troca de tela, o conteúdo entra suavemente (sem recriar a página)
  // Deixa as telas principais prontas em segundo plano: trocar de tela não precisa baixar nada
  useEffect(() => {
    const preparar = () => TELAS_PRINCIPAIS.forEach((tela) => preloadRoute(tela))
    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(preparar, { timeout: 4000 })
      return () => window.cancelIdleCallback?.(id)
    }
    const id = setTimeout(preparar, 2000)
    return () => clearTimeout(id)
  }, [])

  const conteudoRef = useRef(null)
  useEffect(() => {
    const el = conteudoRef.current
    if (!el?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return
    el.animate(
      [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
      { duration: 240, easing: 'ease-out' },
    )
  }, [pathname])
  const [showLogout, setShowLogout] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const [blockedReason, setBlockedReason] = useState('')
  // menu lateral com nomes (aberto) ou só ícones; lembra a escolha
  const [menuAberto, setMenuAberto] = useState(() => { try { return localStorage.getItem('yrm:menu-aberto') !== '0' } catch { return true } })
  const alternarMenu = (v) => {
    const novo = typeof v === 'boolean' ? v : !menuAberto
    setMenuAberto(novo)
    try { localStorage.setItem('yrm:menu-aberto', novo ? '1' : '0') } catch { /* ok */ }
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false)
      }
    }

    const handleBlocked = (e) => {
      setBlocked(true)
      setBlockedReason(e.detail || '')
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    window.addEventListener('company-blocked', handleBlocked)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('company-blocked', handleBlocked)
    }
  }, [])

  return (
    <>
      {isLogged && (
        <>
          <CompanyBlockedOverlay visible={blocked} reason={blockedReason} />
          <TopBar 
            onToggleMobile={() => setMobileMenuOpen(!mobileMenuOpen)}
            isMobile={isMobile}
            menuAberto={menuAberto}
            onAlternarMenu={alternarMenu}
          />
          <S.MainLayout>
            <Menu 
              setLogout={setShowLogout} 
              isMobile={isMobile}
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
              aberto={menuAberto}
              onAlternar={alternarMenu}
            />
            <S.Container
              data-container="main"
              data-fixed-page={TELAS_FIXAS.includes(pathname) ? 'true' : undefined}
              $fixedPage={TELAS_FIXAS.includes(pathname)}
            >
              <LogoutModal open={showLogout} setIsOpen={setShowLogout} />
              <CarregamentoGlobal />
              <S.ContentArea ref={conteudoRef}>
                <Suspense
                  fallback={(
                    <S.RouteFallback aria-live="polite" aria-label="Carregando página">
                      <Loader />
                    </S.RouteFallback>
                  )}
                >
                  <Outlet />
                </Suspense>
              </S.ContentArea>
            </S.Container>
          </S.MainLayout>
          <Sofia />
          <ConvitePush />
        </>
      )}
    </>
  )
}

export default Container
